import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiErrorResponse, ApiResponse, ApiServiceInterface, UserServiceInterface } from '@hutsix/ngxh6';
import { ActivatedRoute } from '@angular/router';
import { PERSONAS, Persona } from '../../../../../demo/personas';

export interface DemoAccount {
    persona: Persona;
    role: string;
    sees: string;
}

/** What each demo log-in is for, keyed by persona key. */
const DEMO_ACCOUNT_NOTES: Record<string, { role: string; sees: string }> = {
    ceo: { role: 'Admin', sees: 'Everything: all four pillars, the Quarterly CEO Report, user management and settings.' },
    coo: { role: 'Management', sees: 'The executive dashboard and every pillar, plus the data assistant.' },
    care: { role: 'Client Services', sees: 'Quality & Client Care: incidents, medications, care plans, registers and referrals.' },
    hr: { role: 'People & Culture', sees: 'People: workforce compliance, online training and HR.' },
    board: { role: 'Board', sees: 'The board dashboard and the summary pages approved for directors.' },
};

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {
    private unsubscribe$ = new Subject<void>();

    public signin: any = {
        email: null,
        password: null,
    };

    public redirect?: string;
    public error?: string;
    public loading = false;

    public readonly demoAccounts: DemoAccount[] = PERSONAS.map(persona => ({
        persona,
        role: DEMO_ACCOUNT_NOTES[persona.key]?.role ?? persona.roleLabels[0] ?? '',
        sees: DEMO_ACCOUNT_NOTES[persona.key]?.sees ?? '',
    }));

    constructor(
        @Inject('ApiService') private api: ApiServiceInterface,
        @Inject('UserService') private userService: UserServiceInterface,
        private cdRef: ChangeDetectorRef,
        private route: ActivatedRoute,
    ) {}

    /** Fill the form with a demo persona and sign straight in (any password works). */
    public signInAs(account: DemoAccount): void {
        if (this.loading) return;
        this.signin = { email: account.persona.email, password: 'demo' };
        this.cdRef.markForCheck();
        this.doSignin();
    }

    public doSignin(): void {
        this.loading = true;
        this.api.post({ url: '/api/user/login_check', data: this.signin, skipAuth: true }).subscribe(
            (res: ApiResponse<any>) => {
                this.error = null;
                this.userService
                    .loginUser(res.data)
                    .pipe(takeUntil(this.unsubscribe$))
                    .subscribe(() => {
                        this.userService.reloadRoute(this.redirect || '/');
                    });
            },
            (err: ApiErrorResponse<any>) => {
                this.loading = false;
                this.error = err.error?.message ?? 'Sign-in failed.';
                this.cdRef.detectChanges();
            },
        );
    }

    ngOnInit(): void {
        this.route.queryParams.subscribe(params => (this.redirect = params.returnUrl || null));

        this.userService.watch.pipe(takeUntil(this.unsubscribe$)).subscribe(user => {
            if (user.loggedIn) this.userService.reloadRoute(this.redirect || '/');
        });
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
